<template>
  <vue-drawer-layout
      ref="drawer"
      :drawer-width="300"
      :enable="false"
      @mask-click="handleMaskClick">
    <div slot="drawer">
      <NavigationDrawer :close="closeDrawer" class="navigation-drawer"/>
    </div>
    <div slot="content" class="root-pane" :class="{'pos-view-layout': isPosView}" @click="handleContentClick">
      <NavigationBar v-if="!isPosView" :toggle="openDrawer" class="nav"/>
      <div class="navigation-route-view">
          <router-view @toggle-drawer="openDrawer"/>
      </div>
    </div>
  </vue-drawer-layout>
</template>

<script>
import NavigationBar from "@/components/layout/navigation/NavigationBar";
import NavigationDrawer from "@/components/layout/navigation/NavigationDrawer";

export default {
  name: "Layout",
  computed: {
    getSaleUnits() {
      return this.$store.getters.getSaleUnits
    },
    isPosView() {
      return this.$route.name === 'pos';
    },
    shortcuts() {
      return this.$store.getters.getShortcuts;
    },
    currentRoute() {
      return this.$route.name;
    },
    customer() {
      return this.$store.getters.getCustomer;
    },
    isSaleProcessing() {
      return this.$store.getters.getSaleProcessingState === true;
    },
    isGlobalLoading() {
      return this.$store.getters.getLoadingState === true;
    },
    isInvoiceProcessingRoute() {
      return this.currentRoute === 'payment' || this.currentRoute === 'split-payment';
    }
  },
  components: {NavigationDrawer, NavigationBar},
  methods: {
    openDrawer() {
      if (!this.getSaleUnits) {
        this.$refs.drawer.toggle();
      }
    },
    closeDrawer() {
      this.$refs.drawer.toggle(false);
    },
    handleMaskClick() {

      this.closeDrawer();
    },
    handleContentClick(event) {
      // Only close if clicking on the mask overlay, not on content
      const drawerElement = this.$refs.drawer.$el;
      if (drawerElement) {
        // CORRECT CLASS NAME: .drawer-mask (not .drawer-layout-mask)
        const mask = drawerElement.querySelector('.drawer-mask');


        // If mask exists and is visible, and click is on the mask (not content)
        if (mask && mask.style.display !== 'none') {
          // Check if click target is the mask itself
          if (event.target === mask) {
            this.closeDrawer();
          }
        }
      }
    },
    /**
     * Handle keyboard shortcuts
     * @param {KeyboardEvent} event - The keyboard event
     */
    handleKeyDown(event) {
      // Ignore shortcuts when typing in input fields
      const isTextInput = event.target && (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.isContentEditable);
      
      if (isTextInput) {
        return;
      }
      
      // Map special keys to standardized names
      const specialKeys = {
        ' ': 'SPACE',
        'Backspace': 'BACK_SPACE',
        'Tab': 'TAB',
        'Enter': 'ENTER',
        'Escape': 'ESCAPE',
        'End': 'END',
        'Home': 'HOME',
        'ArrowLeft': 'LEFT',
        'ArrowUp': 'UP',
        'ArrowRight': 'RIGHT',
        'ArrowDown': 'DOWN',
        'Delete': 'DELETE',
        'Insert': 'INSERT',
        'PageUp': 'PAGE_UP',
        'PageDown': 'PAGE_DOWN',
        'PrintScreen': 'PRINTSCREEN',
        '/': 'SLASH'
      };
      
      // Get standardized key name
      const keyName = specialKeys[event.key] || event.key.toUpperCase();
      
      // Check if shortcuts are loaded
      if (!this.shortcuts) {
        console.warn('[Shortcuts] Shortcuts not loaded yet');
        return;
      }
      
      // Find all actions mapped to this key
      const matchingActions = Object.keys(this.shortcuts).filter(
        action => this.shortcuts[action] === keyName
      );
      
      if (matchingActions.length === 0) return;

      // Hard block shortcuts during invoice processing flow to avoid sticky-key repeats
      if (this.isSaleProcessing || this.isGlobalLoading || this.isInvoiceProcessingRoute) {
        event.preventDefault();
        console.log('[Shortcuts] Ignored while invoice is processing');
        return;
      }
      
      // Resolve conflicts based on current route context
      let shortcutAction = this.resolveShortcutConflict(matchingActions);
      
      if (shortcutAction) {
        event.preventDefault();
        console.log('[Shortcuts] Executing:', shortcutAction, 'Key:', keyName);
        this.executeShortcut(shortcutAction);
      }
    },


    
    
    /**
     * Resolve conflicts when multiple shortcuts are mapped to the same key
     * @param {Array} matchingActions - Array of shortcut action names
     * @returns {String} - The resolved shortcut action
     */
    resolveShortcutConflict(matchingActions) {
      if (matchingActions.length === 1) {
        return matchingActions[0];
      }
      
      const route = this.currentRoute;
      
      // Route-specific priority rules
      const priorityMap = {
        'pos': ['forexPay', 'Cash', 'Card', 'cgratePay', 'Payment', 'splitPay', 'Account', 
                'SaveTable', 'Void', 'VoidAll', 'PriceCheck', 'PriceChange', 'LoadTable'],
        'tables': ['PaySupplier', 'MoneyToSafe'],
        'viewInvoice': ['Reprint', 'Refund']
      };
      
      const priorityActions = priorityMap[route];
      
      if (priorityActions) {
        const match = matchingActions.find(action => priorityActions.includes(action));
        if (match) return match;
      }
      
      // Return first match if no priority rule applies
      return matchingActions[0];
    },
    /**
     * Execute the shortcut action
     * @param {String} action - The shortcut action to execute
     */
    async executeShortcut(action) {
      const route = this.currentRoute;
      
      switch (action) {
        // ==================== FOCUS SHORTCUTS ====================
        case 'forexPay':
          if (route === 'pos') {
            this.findAndCallMethod('focusTotalGiven');
          }
          break;
        // ==================== PAYMENT SHORTCUTS ====================
        case 'Cash':
          if (route === 'pos') {
            this.findAndCallMethod('fastProcessTransaction', 'Cash');
          }
          break;
          
        case 'Card':
          if (route === 'pos') {
            this.findAndCallMethod('fastProcessTransaction', 'Card');
          }
          break;
          
        case 'splitPay':
          if (route === 'pos') {
            this.findAndCallMethod('goToSplitScreen');
          }
          break;
          
        case 'cgratePay':
          if (route === 'pos') {
            this.findAndCallMethod('kazangPayment');
          }
          break;
          
        case 'Payment':
          if (route === 'pos') {
            this.findAndCallMethod('validateAndProcess');
          }
          break;
          
        case 'Account':
          if (route === 'pos' && this.customer) {
            this.processAccountSale();
          } else if (route === 'pos' && !this.customer) {
            this.$utils.showWarning('Please select a customer for account sale');
          }
          break;
          
        // ==================== TABLE MANAGEMENT ====================
        case 'LoadTable':
          this.$router.push({ name: 'tables' });
          break;
          
        case 'SaveTable':
          if (route === 'pos') {
            this.findAndCallMethod('saveTable');
          }
          break;
          
        // ==================== TRANSACTION ACTIONS ====================
        case 'Void':
          if (route === 'pos') {
            this.voidLastItem();
          }
          break;
          
        case 'VoidAll':
          if (route === 'pos') {
            this.voidAllItems();
          }
          break;
          
        // ==================== PRICE OPERATIONS ====================
        case 'PriceCheck':
          if (route === 'pos') {
            this.findAndCallMethod('openItemSearchModal');
          }
          break;
          
        case 'PriceChange':
          if (route === 'pos') {
            this.findAndCallMethod('editSelectedLinePrice');
          }
          break;
          
        // ==================== DRAWER OPERATIONS ====================
        case 'OpenDrwer':
          const isAllowed = await this.$store.dispatch('validateSecurityPermission', 'OpenDrwer');
          if (isAllowed) {
            await this.$store.dispatch('openCashDrawer');
          }
          break;
          
        // ==================== INVOICE OPERATIONS ====================
        case 'Reprint':
          if (route === 'viewInvoice') {
            this.findAndCallMethod('reprintInvoice');
          }
          break;
          
        case 'Refund':
          if (route === 'viewInvoice') {
            this.findAndCallMethod('openRefund');
          }
          break;
          
        // ==================== TABLE SCREEN OPERATIONS ====================
        case 'PaySupplier':
          if (route === 'tables') {
            this.findAndCallMethod('doPayout');
          }
          break;
          
        case 'MoneyToSafe':
          if (route === 'tables') {
            this.findAndCallMethod('cashDrop');
          }
          break;
          
        // ==================== NAVIGATION SHORTCUTS ====================
        case 'CashUp':
          this.$router.push({ name: 'cashup' });
          break;
          
        case 'LogOff':
          // Confirm before logging out
          if (typeof Swal !== 'undefined') {
            Swal.fire({
              title: 'Log out?',
              text: 'Are you sure you want to log out of POS?'
              , icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Yes, log out',
              cancelButtonText: 'Cancel',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then((res) => {
              if (res.isConfirmed) {
                this.$store.dispatch('posLogout');
              }
            });
          } else {
            if (confirm('Are you sure you want to log out of POS?')) {
              this.$store.dispatch('posLogout');
            }
          }
          break;
          
        case 'ExitPos':
          // Confirm before closing the application
          if (typeof Swal !== 'undefined') {
            Swal.fire({
              title: 'Exit application?',
              text: 'Are you sure you want to exit Lyt-ning POS?'
              , icon: 'warning',
              showCancelButton: true,
              confirmButtonText: 'Yes, exit',
              cancelButtonText: 'Cancel',
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then((res) => {
              if (res.isConfirmed) {
                this.closeApplication();
              }
            });
          } else {
            if (confirm('Are you sure you want to exit Lyt-ning POS?')) {
              this.closeApplication();
            }
          }
          break;
          
        // ==================== FUTURE IMPLEMENTATION ====================
        case 'CashBack':
          console.log('[Shortcuts] CashBack not yet implemented');
          break;
          
        default:
          console.warn('[Shortcuts] Unknown shortcut action:', action);
      }
    },
    
    /**
     * Close the application
     */
    closeApplication() {
      if (window.electron) {
        window.electron.closeApp();
      } else if (window.close) {
        window.close();
      } else {
        console.warn('[Shortcuts] Cannot close application - not in Electron or browser context');
      }
    },
    /**
     * Find and call a method in child components
     * Searches through the component tree to find and execute a method
     * @param {String} methodName - The name of the method to call
     * @param {...*} args - Arguments to pass to the method
     */
    findAndCallMethod(methodName, ...args) {
      // First try to find the method in the current view component
      const routerView = this.$el.querySelector('.navigation-route-view > *');
      if (routerView && routerView.__vue__ && 
          routerView.__vue__[methodName] && 
          typeof routerView.__vue__[methodName] === 'function') {
        routerView.__vue__[methodName](...args);
        return;
      }
      
      // If not found in the current view, search recursively through all components
      const findComponentWithMethod = (component) => {
        if (!component) return null;
        
        // Check if the component has the method
        if (component[methodName] && typeof component[methodName] === 'function') {
          return component;
        }
        
        // Check children recursively
        if (component.$children && component.$children.length > 0) {
          for (const child of component.$children) {
            const found = findComponentWithMethod(child);
            if (found) return found;
          }
        }
        
        return null;
      };
      
      const component = findComponentWithMethod(this.$root);
      if (component) {
        component[methodName](...args);
      } else {
        console.warn(`[Shortcuts] Method ${methodName} not found in any component`);
      }
    },
    
    /**
     * Void the last item in the current sale
     */
    voidLastItem() {
      const saleLines = this.$store.getters.getSaleLines;
      if (saleLines && saleLines.length > 0) {
        const lastItem = saleLines[saleLines.length - 1];
        this.$store.commit('voidLine', lastItem);
        console.log('[Shortcuts] Voided last item:', lastItem.Description);
      } else {
        console.warn('[Shortcuts] No items to void');
      }
    },
    
    /**
     * Process an account sale for the selected customer
     */
    processAccountSale() {
      if (this.customer) {
        this.findAndCallMethod('processTransaction', 'AccountSale');
      } else {
        this.$utils.showWarning('Please select a customer for account sale');
      }
    },
    
    /**
     * Void all items in the current sale
     */
    voidAllItems() {
      const saleLines = this.$store.getters.getSaleLines;
      if (saleLines && saleLines.length > 0) {
        const itemCount = saleLines.length;
        saleLines.forEach(item => {
          this.$store.commit('voidLine', item);
        });
        console.log(`[Shortcuts] Voided all ${itemCount} items`);
      } else {
        console.warn('[Shortcuts] No items to void');
      }
    }
  },
  mounted() {
    // Add event listener for keyboard shortcuts
    window.addEventListener('keydown', this.handleKeyDown);

    // Add direct click handler to the drawer mask
    this.$nextTick(() => {
      const drawerElement = this.$refs.drawer?.$el;
      if (drawerElement) {
        // CORRECT CLASS NAME: .drawer-mask (not .drawer-layout-mask)
        const mask = drawerElement.querySelector('.drawer-mask');

        if (mask) {
          mask.addEventListener('click', this.closeDrawer);
        }
      }
    });
  },
  beforeDestroy() {
    // Remove event listener
    window.removeEventListener('keydown', this.handleKeyDown);

    // Remove mask click handler
    const drawerElement = this.$refs.drawer?.$el;
    if (drawerElement) {
      // CORRECT CLASS NAME: .drawer-mask (not .drawer-layout-mask)
      const mask = drawerElement.querySelector('.drawer-mask');
      if (mask) {
        mask.removeEventListener('click', this.closeDrawer);
      }
    }
  },
  watch: {
    isPosView: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          document.body.classList.add('pos-view-active');
        } else {
          document.body.classList.remove('pos-view-active');
        }
      }
    }
  },


}
</script>

<style lang="scss" scoped>
.root-pane {
  display: flex;
  flex-direction: column;
  height: 100vh; // Full viewport height
  overflow: hidden; // Prevent body scroll

  &.pos-view-layout {
    .navigation-route-view {
      margin-top: -100px;
      height: 100vh; // Full height for POS view
    }
    .no-margin-top {
      margin-top: 0 !important;
    }
  }
}

.nav {
  flex-shrink: 0; // Prevent navigation from shrinking
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
}

.navigation-route-view {
  flex: 1; // Take all remaining space
  overflow-y: auto; // Make this scrollable

}

.navigation-drawer {
  min-width: 285px;
  width: 100%;
  height: 100vh;
}



.offline-banner {
  position: sticky;
  top: 0;
  left: 0;
  padding: 5px;
  display: block;
  text-align: center;
  width: 100%;
  background: rgb(220, 53, 69);
  color: white;
  right: 0;
}
</style>
